/**
 * MCP Settings page (2D.3 + 2D.8 partial).
 *
 * Admin toggle for AI booking. Shows MCP endpoint URL,
 * connection instructions, and tool configuration.
 */
import { useState, useEffect } from "react";
import { apiFetch } from "@/lib/api-client";
import { cn } from "@/lib/utils";
import { Skeleton, SkeletonCard } from "@/components/skeleton";

interface McpSettings {
  mcpCustomerEnabled: boolean;
  showPricing: boolean;
  showCreditPackages: boolean;
  maxAiBookingsPerDay: number;
  requireHumanConfirmation: boolean;
  coarseAvailabilityOnly: boolean;
  businessDescription: string | null;
}

export default function McpSettingsPage() {
  const [settings, setSettings] = useState<McpSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  useEffect(() => {
    apiFetch<{ data: McpSettings }>("/platform/mcp-settings")
      .then((res) => setSettings(res.data))
      .catch(() => {
        // Default settings if endpoint not wired yet
        setSettings({
          mcpCustomerEnabled: false,
          showPricing: true,
          showCreditPackages: false,
          maxAiBookingsPerDay: 10,
          requireHumanConfirmation: true,
          coarseAvailabilityOnly: true,
          businessDescription: null,
        });
      })
      .finally(() => setIsLoading(false));
  }, []);

  const handleSave = async () => {
    if (!settings) return;
    setIsSaving(true);
    setSaveMessage(null);
    try {
      await apiFetch("/platform/mcp-settings", {
        method: "PUT",
        body: JSON.stringify(settings),
      });
      setSaveMessage("Settings saved");
      setTimeout(() => setSaveMessage(null), 3000);
    } catch {
      setSaveMessage("Failed to save");
    } finally {
      setIsSaving(false);
    }
  };

  const updateField = <K extends keyof McpSettings>(key: K, value: McpSettings[K]) => {
    setSettings((prev) => prev ? { ...prev, [key]: value } : prev);
  };

  if (isLoading || !settings) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <SkeletonCard className="h-40" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-foreground">AI Booking (MCP)</h2>
        <p className="text-sm text-muted-foreground">
          Let AI agents book appointments, browse services, and interact with your business.
        </p>
      </div>

      {/* Enable toggle */}
      <div className="stagger-item rounded-lg border border-border bg-card p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-foreground">Enable AI Booking</p>
            <p className="text-xs text-muted-foreground">
              When enabled, AI agents can discover and interact with your business
            </p>
          </div>
          <button
            onClick={() => updateField("mcpCustomerEnabled", !settings.mcpCustomerEnabled)}
            className={cn(
              "relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full transition-colors",
              settings.mcpCustomerEnabled ? "bg-primary" : "bg-muted",
            )}
          >
            <span
              className={cn(
                "inline-block h-5 w-5 transform rounded-full bg-background shadow transition-transform",
                settings.mcpCustomerEnabled ? "translate-x-5" : "translate-x-0.5",
              )}
              style={{ marginTop: "2px" }}
            />
          </button>
        </div>
      </div>

      {settings.mcpCustomerEnabled && (
        <>
          {/* Safety settings */}
          <div className="stagger-item rounded-lg border border-border bg-card p-4 space-y-4">
            <h3 className="text-sm font-semibold text-foreground">Safety & Privacy</h3>

            <ToggleSetting
              label="Require human confirmation"
              description="AI bookings need your approval before being confirmed"
              value={settings.requireHumanConfirmation}
              onChange={(v) => updateField("requireHumanConfirmation", v)}
            />

            <ToggleSetting
              label="Coarse availability only"
              description="Show only morning/afternoon/evening blocks to anonymous AI agents"
              value={settings.coarseAvailabilityOnly}
              onChange={(v) => updateField("coarseAvailabilityOnly", v)}
            />

            <ToggleSetting
              label="Show pricing"
              description="Let AI agents see your service prices"
              value={settings.showPricing}
              onChange={(v) => updateField("showPricing", v)}
            />

            <div>
              <label className="text-xs text-foreground font-medium">Max AI bookings per day</label>
              <input
                type="number"
                min={1}
                max={100}
                value={settings.maxAiBookingsPerDay}
                onChange={(e) => updateField("maxAiBookingsPerDay", Number(e.target.value))}
                className="mt-1 block w-24 rounded-md border border-input bg-background px-3 py-1.5 text-sm"
              />
            </div>
          </div>

          {/* Business description */}
          <div className="stagger-item rounded-lg border border-border bg-card p-4">
            <h3 className="mb-2 text-sm font-semibold text-foreground">AI Business Description</h3>
            <p className="mb-2 text-xs text-muted-foreground">
              This description helps AI agents understand what your business offers.
            </p>
            <textarea
              value={settings.businessDescription ?? ""}
              onChange={(e) => updateField("businessDescription", e.target.value || null)}
              placeholder="e.g., We're a yoga studio in downtown Bucharest offering group classes and private sessions..."
              className="w-full resize-none rounded-md border border-input bg-background p-3 text-sm"
              rows={3}
            />
          </div>
        </>
      )}

      {/* Save button */}
      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
        >
          {isSaving ? (
            <span className="inline-flex items-center gap-2">
              <Skeleton className="h-4 w-4 bg-white/50" />
              <Skeleton className="h-4 w-16 bg-white/50" />
            </span>
          ) : "Save Settings"}
        </button>
        {saveMessage && (
          <p className={cn("text-sm", saveMessage.includes("Failed") ? "text-destructive" : "text-[var(--color-success)]")}>
            {saveMessage}
          </p>
        )}
      </div>
    </div>
  );
}

function ToggleSetting({
  label,
  description,
  value,
  onChange,
}: {
  label: string;
  description: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="stagger-item flex items-center justify-between">
      <div>
        <p className="text-sm text-foreground">{label}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <button
        onClick={() => onChange(!value)}
        className={cn(
          "relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full transition-colors",
          value ? "bg-primary" : "bg-muted",
        )}
      >
        <span
          className={cn(
            "inline-block h-4 w-4 transform rounded-full bg-background shadow transition-transform",
            value ? "translate-x-4" : "translate-x-0.5",
          )}
          style={{ marginTop: "2px" }}
        />
      </button>
    </div>
  );
}
